import { useEffect, useState } from "react";
import { motion } from "framer-motion";

const sections = [
  { id: "hero", label: "Home" },
  { id: "results", label: "Results" },
  { id: "foundation", label: "The Foundation" },
  { id: "services", label: "Services" },
  { id: "roi-calculator", label: "ROI Calculator" },
  { id: "how-it-works", label: "How It Works" },
  { id: "why-fuzionlabs", label: "Why FuzionLabs" },
  { id: "sound-familiar", label: "Sound Familiar?" },
  { id: "free-audit", label: "Free Audit" },
];

export function SideNav() {
  const [active, setActive] = useState("hero");
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  useEffect(() => {
    const getDocTop = (el: HTMLElement) => {
      let y = 0;
      let node: HTMLElement | null = el;
      while (node) {
        y += node.offsetTop;
        node = node.offsetParent as HTMLElement | null;
      }
      return y;
    };
    const computeActive = () => {
      const lineY = window.scrollY + window.innerHeight * 0.25;
      let current = sections[0].id;
      const sorted = sections
        .map((s) => ({ id: s.id, el: document.getElementById(s.id) }))
        .filter((x): x is { id: string; el: HTMLElement } => !!x.el)
        .map((x) => ({ id: x.id, y: getDocTop(x.el) }))
        .sort((a, b) => a.y - b.y);
      for (const s of sorted) {
        if (s.y <= lineY) current = s.id;
      }
      setActive(current);
    };
    computeActive();
    window.addEventListener("scroll", computeActive, { passive: true });
    window.addEventListener("resize", computeActive);
    return () => {
      window.removeEventListener("scroll", computeActive);
      window.removeEventListener("resize", computeActive);
    };
  }, []);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <nav className="fixed left-4 top-1/2 -translate-y-1/2 z-50 hidden lg:flex flex-col items-center gap-3">
      <div className="w-px h-4 bg-border" />
      {sections.map((s) => (
        <div key={s.id} className="relative flex items-center">
          {hoveredId === s.id && (
            <motion.span
              initial={{ opacity: 0, x: -4 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0 }}
              className="absolute left-5 whitespace-nowrap text-xs text-muted-foreground bg-card border border-border rounded px-2 py-0.5 shadow-sm"
            >
              {s.label}
            </motion.span>
          )}
          <button
            onClick={() => scrollTo(s.id)}
            onMouseEnter={() => setHoveredId(s.id)}
            onMouseLeave={() => setHoveredId(null)}
            className={`w-2 h-2 rounded-full transition-all duration-200 ${
              active === s.id
                ? "bg-primary scale-150"
                : "bg-muted-foreground/30 hover:bg-muted-foreground/60"
            }`}
            aria-label={`Scroll to ${s.label}`}
          />
        </div>
      ))}
      <div className="w-px h-4 bg-border" />
    </nav>
  );
}
